// DOM helpers shared by the recorder and replayer. All take an explicit `win`
// so they work under jsdom and inside a deployed app's own window.

// Is the element present and not obviously hidden? jsdom does no layout, so we
// intentionally rely on computed display/visibility/opacity and the `hidden`
// attribute rather than offset boxes / getBoundingClientRect.
function isVisible(el) {
    if (!el || el.nodeType !== 1) return false;
    if (el.hasAttribute && el.hasAttribute('hidden')) return false;
    const doc = el.ownerDocument;
    const win = doc && doc.defaultView;
    if (win && typeof win.getComputedStyle === 'function') {
        const s = win.getComputedStyle(el);
        if (s && (s.display === 'none' || s.visibility === 'hidden' || s.opacity === '0')) return false;
    }
    // Walk up: a hidden ancestor hides this element too.
    let p = el.parentElement;
    while (p) {
        if (p.hasAttribute && p.hasAttribute('hidden')) return false;
        if (win && typeof win.getComputedStyle === 'function') {
            const ps = win.getComputedStyle(p);
            if (ps && (ps.display === 'none' || ps.visibility === 'hidden')) return false;
        }
        p = p.parentElement;
    }
    return true;
}

// Wait until `predicate()` returns a truthy value (returned to the caller) or
// reject after `timeout` ms. Uses a MutationObserver for reactivity plus a
// low-frequency poll as a safety net for changes MOs don't surface (e.g. a
// value flipped without a DOM mutation).
function waitFor(win, doc, predicate, opts) {
    const timeout = (opts && opts.timeout) != null ? opts.timeout : 5000;
    const interval = (opts && opts.interval) != null ? opts.interval : 50;
    return new Promise((resolve, reject) => {
        let done = false;
        let obs = null;
        let poll = null;
        let timer = null;

        const cleanup = () => {
            if (obs) try { obs.disconnect(); } catch (_) { /* noop */ }
            if (poll != null) win.clearInterval(poll);
            if (timer != null) win.clearTimeout(timer);
        };
        const check = () => {
            if (done) return true;
            let r;
            try { r = predicate(); } catch (_) { r = null; }
            if (r) {
                done = true;
                cleanup();
                resolve(r);
                return true;
            }
            return false;
        };

        if (check()) return;

        const MO = win.MutationObserver;
        if (MO) {
            obs = new MO(() => { check(); });
            const rootNode = doc.documentElement || doc.body || doc;
            obs.observe(rootNode, { childList: true, subtree: true, attributes: true, characterData: true });
        }
        poll = win.setInterval(check, interval);
        timer = win.setTimeout(() => {
            if (done) return;
            done = true;
            cleanup();
            reject(new Error('waitFor: timed out after ' + timeout + 'ms'));
        }, timeout);
    });
}

// Set an input/select/textarea value the way React tolerates: call the value
// setter on the *prototype* (React patches the instance's own `value` property
// to track changes; the prototype setter bypasses that patch so React's
// internal value tracker still sees the update when we dispatch `input`).
function setNativeValue(el, value) {
    const proto = Object.getPrototypeOf(el);
    const protoDesc = proto ? Object.getOwnPropertyDescriptor(proto, 'value') : null;
    if (protoDesc && typeof protoDesc.set === 'function') {
        protoDesc.set.call(el, value);
    } else {
        el.value = value;
    }
}

function dispatch(win, el, type) {
    const Ev = win.Event || (typeof Event !== 'undefined' ? Event : null);
    if (!Ev) return;
    el.dispatchEvent(new Ev(type, { bubbles: true, cancelable: true }));
}

function fireClick(win, el) {
    if (typeof el.click === 'function') {
        el.click();
        return;
    }
    const ME = win.MouseEvent || win.Event;
    el.dispatchEvent(new ME('click', { bubbles: true, cancelable: true }));
}

function sleep(win, ms) {
    return new Promise((resolve) => win.setTimeout(resolve, ms));
}

module.exports = { isVisible, waitFor, setNativeValue, dispatch, fireClick, sleep };
